import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { DataTable } from '../components/DataTable'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'

const useLinkedHouseholds = () =>
  useQuery({
    queryKey: ['admin', 'linked-households'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('linked_households')
        .select(`
          id, status, created_at,
          primary:households!linked_households_primary_household_id_fkey(name),
          linked:households!linked_households_linked_household_id_fkey(name),
          payer:profiles!linked_households_linked_by_fkey(email)
        `)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

const StatusBadge = ({ status }) => {
  const cls = {
    active:  'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    revoked: 'bg-rose-100 text-rose-700',
  }[status] ?? 'bg-background text-text-muted'
  return (
    <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize', cls)}>
      {status}
    </span>
  )
}

export const LinkedHouseholdsPage = () => {
  const qc = useQueryClient()
  const { data: links = [], isLoading } = useLinkedHouseholds()

  const revoke = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('linked_households')
        .update({ status: 'revoked' })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'linked-households'] }),
  })

  const columns = [
    {
      key: 'primary', label: 'Primary Household',
      render: (_, row) => row.primary?.name ?? '—',
    },
    {
      key: 'linked', label: 'Linked Household',
      render: (_, row) => row.linked?.name ?? '—',
    },
    {
      key: 'payer', label: 'Linked by',
      render: (_, row) => row.payer?.email ?? '—',
    },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (v) => <StatusBadge status={v} />,
    },
    {
      key: 'created_at', label: 'Created', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—',
    },
    {
      key: 'actions', label: '',
      render: (_, row) => {
        if (row.status === 'revoked') return null
        return (
          <button
            onClick={() => revoke.mutate(row.id)}
            disabled={revoke.isPending}
            className="text-xs text-rose-500 hover:text-rose-700 font-medium"
          >
            {revoke.isPending ? <Spinner size="sm" /> : 'Revoke'}
          </button>
        )
      },
    },
  ]

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Linked Households</h1>
        <p className="text-sm text-text-muted mt-1">
          {links.filter((l) => l.status === 'active').length} active pairs
        </p>
      </div>
      <DataTable
        columns={columns}
        data={links}
        loading={isLoading}
        emptyMessage="No linked households found."
      />
    </div>
  )
}
